import { mkdir, readFile, writeFile } from 'node:fs/promises'
import { homedir } from 'node:os'
import { join } from 'node:path'

export interface CliConfig {
  /** Base API URL (the gateway), e.g. https://api.localtest.me:8443 */
  apiUrl?: string
  /** Verentis API key (vrt_ prefix) — sent directly as a bearer credential. */
  apiKey?: string
  /** Identity token — exchanged for audience-scoped access tokens per request. */
  identityToken?: string
  /** Refresh token for renewing the identity token (device-code login). */
  refreshToken?: string
  /** Default workspace for workspace-scoped commands (files, exec, install, dev). */
  defaultWorkspaceId?: string
  /** Default account for admin commands (users, api-keys, invitations). */
  defaultAccountId?: string
  /** Name of the default signing key (file under ~/.verentis/keys). */
  defaultKey?: string
  /** Base64 X25519 public key of the platform sealing recipient (for `pack --encrypt`). */
  sealingPublicKey?: string
}

export const configDir = () => process.env.VERENTIS_CONFIG_DIR ?? join(homedir(), '.verentis')
export const configPath = () => join(configDir(), 'config.json')
export const keysDir = () => join(configDir(), 'keys')

export async function loadConfig(): Promise<CliConfig> {
  try {
    return JSON.parse(await readFile(configPath(), 'utf8')) as CliConfig
  } catch {
    return {}
  }
}

export async function saveConfig(config: CliConfig): Promise<void> {
  await mkdir(configDir(), { recursive: true, mode: 0o700 })
  await writeFile(configPath(), JSON.stringify(config, null, 2) + '\n', { mode: 0o600 })
}

export function requireApiUrl(config: CliConfig): string {
  const url = process.env.VERENTIS_API_URL ?? config.apiUrl
  if (!url) {
    throw new Error('No API URL configured. Run `verentis login --api-url <url>` first (or set VERENTIS_API_URL).')
  }
  return url.replace(/\/+$/, '')
}
